/*
feature name: <kebab-case> --> analytics table

const flagConfig = {
	'feature-1-name': { probability: 0.5 }, <-- percentage of probability (from 0 to 1)
	'feature-2-name': { probability: 0.5, deps: ['feature-1-name'] }, <-- dependencies
    'feature-3-name': { probability: 0.5, opts: add cookie options }
}

usage:
	this.$isFlag('feature-1-name')
*/

import Cookies from 'js-cookie'

const isAfterStartAt = startAt => {
	if (!startAt) return true

	const startAtMs = Date.parse(startAt)
	if (!Number.isFinite(startAtMs)) {
		console.error(`Invalid feature flag startAt "${startAt}". Use an ISO timestamp.`)
		return false
	}

	return Date.now() >= startAtMs
}

const flagConfig = {
	'example-flag': { probability: 0.5 },
	'landing-page-v6-vs-v9.1': { probability: 0, opts: { expires: 365 } },
}

const getStoredFlag = name => {
	if (flagConfig[name]?.scope === 'session') {
		return window.sessionStorage.getItem(name)
	}

	return Cookies.get(name)
}

const setStoredFlag = (name, value, opts) => {
	if (flagConfig[name]?.scope === 'session') {
		window.sessionStorage.setItem(name, value)
		return
	}

	Cookies.set(name, value, opts)
}

// Intermediate independent state without considering for dependencies [DO NOT USE OUTSIDE OF THIS SCRIPT]
for (const key of Object.keys(flagConfig)) {
	if (flagConfig[key] === undefined) {
		console.error('Feature flag is not defined: ', key)
		flagConfig[key].state = false
	} else {
		// Math.random is 0 inclusive and 1 exclusive
		flagConfig[key].state = Math.random() < flagConfig[key].probability
	}
}

const checkFlag = name => {
	const flagData = flagConfig[name]
	const deps = flagData.deps || []
	const storedFlag = getStoredFlag(name)

	if (storedFlag) {
		return storedFlag === 'true'
	}

	// Turn off flag if it doesn't have dependencies
	for (let i = 0; i < deps.length; i++) {
		if (flagConfig[deps[i]] === undefined) {
			console.error('Unknown flag dependency', deps[i])
			return false
		}
		if (flagConfig[deps[i]].state === false) {
			return false
		}
	}

	try {
		if (!flagData) {
			console.error('Unknown feature flag, ', name)
			return false
		}
		if (!isAfterStartAt(flagData.startAt)) {
			return false
		}
		return flagData.state
	} catch (error) {
		console.error(error)
		return false
	}
}

const computedFlags = {}
const computedFlagData = {}

for (const key of Object.keys(flagConfig)) {
	const value = checkFlag(key)
	const opts = flagConfig[key].opts
	computedFlags[key] = value
	computedFlagData[key] = {
		experiment_variant: value ? 1 : 0,
	}
	setStoredFlag(key, value, opts)
}

// eslint-disable-next-line no-underscore-dangle
const isFlag_ = name => {
	if (computedFlags[name] === undefined) {
		console.error('Feature flag is not defined: ', name)
		return false
	}
	return computedFlags[name]
}

export const getFlags = () => computedFlagData
export const isFlag = isFlag_

export default isFlag
